import { WabunkaError_Source } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';

import type { Error as OtonamiError, Error_Type } from '@japan-culture-and-technology/calchez-protos/ts_proto/error';
import type { WabunkaError } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';

export interface ValidationErrorObj {
  fieldName?: string;
  message: string;
}
export class ValidationError extends Error {
  errors: ValidationErrorObj[];

  constructor(errors: ValidationErrorObj[], message: string = 'ValidationError') {
    super(message);
    this.errors = errors;
    this.name = 'ValidationError';
  }
}

export class OtonamiAPIError extends Error {
  status: number;
  type: Error_Type;
  constructor(error: OtonamiError) {
    super(error.message);
    this.status = error.status;
    this.type = error.type;
    this.name = 'OtonamiAPIError';
  }

  getType(): Error_Type {
    return this.type;
  }

  getMessage(): string {
    return this.message;
  }
}

// RESERVATION_TYPE_CHANGED_ERROR_CODE 予約種別変更エラーの機械可読コード（backend の ErrCodeReservationTypeChanged と一致）。
// 予約確定時に種別が Request へ変わっており Pay.jp では確定できない場合、backend が 409 + この code を返す。
// frontend はこれを検知して Fincode へ自動切替し、フォームを保持したままカード再入力を促す。
export const RESERVATION_TYPE_CHANGED_ERROR_CODE = 'RESERVATION_TYPE_CHANGED';

export class WabunkaAPIError extends Error {
  status: number;
  source: WabunkaError_Source;
  metadata?: Record<string, unknown>;
  constructor(error: WabunkaError) {
    super(error.message);
    this.status = error.status;
    this.source = error.source;
    this.metadata = error.metadata as Record<string, unknown> | undefined;
    this.name = 'WabunkaAPIError';
  }

  getSource(): WabunkaError_Source {
    return this.source;
  }

  getMessage(): string {
    return this.message;
  }

  // getCode は response.metadata.code（機械可読なエラーコード）を返す。無い場合は undefined。
  getCode(): string | undefined {
    const code = this.metadata?.code;
    return typeof code === 'string' ? code : undefined;
  }
}

/**
 * WabunkaError 形式であるかを確認する Type Guard
 * @param data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isWabunkaError = (data: any): data is WabunkaError => {
  return (
    data !== null &&
    typeof data === 'object' &&
    typeof data.status === 'number' &&
    !isNaN(data.status) &&
    typeof data.message === 'string' &&
    typeof data.source === 'number' &&
    (Object.values(WabunkaError_Source) as (string | number)[]).includes(data.source)
  );
};

/**
 * エラーの配列と、フィールド名を引数に取り、該当するエラーメッセージを返却する
 * 該当するフィールドがない場合には、空文字を返却する
 * @param {Array<ValidationErrorObj>} errors - エラー
 * @param {string} fieldName - フィールド名
 * @returns {string} - エラーメッセージ
 */
export const getMessageByFieldName = (errors: ValidationErrorObj[], fieldName: string): string => {
  const error = errors.find((_) => _.fieldName === fieldName);

  if (error === undefined) return '';

  return error.message;
};

export class IllegalArgumentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalArgumentError';
  }
}
